/**
 * Global "upgrade to do this" popup — a tiny store so ANY code (React or not,
 * e.g. the axios interceptor) can pop the paywall in place instead of a jarring
 * full-page redirect. A single <UpgradeModal/> (mounted in V2App) renders it.
 *
 * Used for the free-tier CREATION caps (1 project / avatar / voice / brand kit,
 * lifetime — no delete-and-recreate): hitting a cap shows this popup, which links
 * to /upgrade. (Free VIDEO generation is a different flow — the blurred preview
 * funnel — not this popup.)
 */
import { create } from 'zustand';

export type UpgradeContext =
  | { kind: 'free_asset_limit'; asset: 'avatar' | 'voice' | 'brand' }
  | { kind: 'free_project_limit'; existingOrderId?: string | null; existingVideoProvider?: string | null }
  | { kind: 'insufficient_credits' }
  | { kind: 'pro_feature' };

/** Analytics context id for an UpgradeContext — matches the `?context=` value
 *  the modal's CTA navigates to, so impression and pricing-page events join. */
export function upgradeContextId(ctx: UpgradeContext | null): string {
  switch (ctx?.kind) {
    case 'free_asset_limit': return `free_${ctx.asset}_limit`;
    case 'free_project_limit': return 'free_project_limit';
    case 'insufficient_credits': return 'insufficient_credits';
    default: return 'pro_feature';
  }
}

interface UpgradeModalState {
  open: boolean;
  context: UpgradeContext | null;
  show: (context: UpgradeContext) => void;
  hide: () => void;
}

export const useUpgradeModal = create<UpgradeModalState>((set) => ({
  open: false,
  context: null,
  // No analytics here — owner taxonomy: 'Paywall Viewed' fires only when the
  // user lands on /subscription (this popup's CTA routes there).
  show: (context) => set({ open: true, context }),
  hide: () => set({ open: false, context: null }),
}));

/** True for the free-tier creation-cap error (403 free_tier_limit) — so a create
 *  modal can close itself silently (the global popup already explains it). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFreeLimitError(e: any): boolean {
  return e?.response?.status === 403 && e?.response?.data?.error === 'free_tier_limit';
}

/** Rejected with by the v2Service interceptor when the user DECLINES the
 *  "this costs N credits" confirmation. Not a real API failure — callers should
 *  swallow it silently (no error toast), just stop the action. */
export class CreditDeclinedError extends Error {
  readonly __creditDeclined = true;
  constructor() {
    super('credit_charge_declined');
    this.name = 'CreditDeclinedError';
  }
}

/** True when the user cancelled the credit-charge confirmation. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isCreditDeclined(e: any): boolean {
  return e instanceof CreditDeclinedError || e?.__creditDeclined === true;
}
